import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ConsoleMembershipEditQuery,
  ConsoleMembershipEditQueryVariables,
  ConsolePlanDetailFragmentDoc,
  ConsoleProductDetailFragmentDoc,
  MembershipInterval,
  UpdateSubscriptionInput,
  UpdateSubscriptionMutation,
  UpdateSubscriptionMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import {
  getActiveMembershipSubscriptionProductPlan,
  MembershipSubscriptionProduct,
} from '@/core/membership'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { t } = useTranslation('consoleMembershipEdit')
  const { showMessage } = useMessage()
  const { router, routerPages } = useRouter<{ planId: string }>()
  const planIds = router.query.planId.split(':')

  const { data } = useQuery<
    ConsoleMembershipEditQuery,
    ConsoleMembershipEditQueryVariables
  >(QUERY)

  const [updateSubscription] = useMutation<
    UpdateSubscriptionMutation,
    UpdateSubscriptionMutationVariables
  >(UPDATE_SUBSCRIPTION)

  const product = useMemo(() => {
    return getProductByPlanId(data?.artist, planIds[0])
  }, [data?.artist])

  return {
    state: {
      artistId: user?.uid ?? '',
      product,
    },
    actions: {
      async onSubmit({ content, title, annualContent }) {
        const productId = product?.productId
        const inputs: UpdateSubscriptionInput = {
          title,
          content,
          annualContent,
        }
        if (productId) {
          inputs.productId = productId
        } else {
          inputs.planId = planIds[0]
          inputs.productId = data?.artist?.subscription?.productId
        }
        await updateSubscription({
          variables: {
            artistId: user?.uid ?? '',
            inputs,
          },
        })
        showMessage(t('updateSubscriptionSuccess'))
        router.goBack(routerPages.ConsoleMembership)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMembershipEdit {
    artist {
      id
      subscription {
        productId
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
`

const UPDATE_SUBSCRIPTION = gql`
  mutation UpdateSubscription(
    $artistId: ID!
    $inputs: UpdateSubscriptionInput!
  ) {
    updateSubscription(artistId: $artistId, inputs: $inputs) {
      id
      subscription {
        productId
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
`

export function getProductByPlanId(
  artist: ConsoleMembershipEditQuery['artist'],
  planId: string
): MembershipSubscriptionProduct | null | undefined {
  if (artist?.subscription?.plans?.length) {
    const plan = artist.subscription.plans.find(plan => plan.planId === planId)
    if (!plan) {
      return null
    }
    return {
      productId: undefined,
      content: plan.content,
      title: plan.title,
      plan: {
        ...plan,
        interval: MembershipInterval.Month,
      },
      plans: [
        {
          ...plan,
          interval: MembershipInterval.Month,
        },
      ],
    }
  }
  if (artist?.subscription?.products?.length) {
    return getActiveMembershipSubscriptionProductPlan(artist, planId)
  }
  return null
}
